import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  collection,
  doc,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection, useDocument } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { Card, EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { StatusChip } from '@/components/ui/StatusChip';
import { BookingStatusStepper } from '@/components/booking/BookingStatusStepper';
import { BookingSlotPicker } from '@/components/booking/BookingSlotPicker';
import { PriceBreakdown } from '@/components/booking/PriceBreakdown';
import { DisputeModal } from '@/components/booking/DisputeModal';
import { BookingSafetyPanel } from '@/components/booking/BookingSafetyPanel';
import { BookingMessages } from '@/components/booking/BookingMessages';
import { InstitutionBadge } from '@/components/trust/InstitutionBadge';
import { ReviewModal } from '@/components/booking/ReviewModal';
import { ReviewLongevitySheet } from '@/components/booking/ReviewLongevitySheet';
import { ReportModal } from '@/components/moderation/ReportModal';
import { toast } from '@/components/ui/Toast';
import { formatBookingWhen } from '@/lib/formatRelative';
import {
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_TONE,
  bookingNextAction,
  paymentStatusCopy,
} from '@/lib/bookingLabels';
import {
  callAcceptBooking,
  callAcceptCounterProposal,
  callCancelBooking,
  callConfirmSafetyCheckIn,
  callConfirmCompleted,
  callDeclineBooking,
  callMarkInProgress,
  callMarkServiceDone,
  callPayBooking,
  callProposeBookingTime,
  callVerifyPayment,
} from '@/lib/callables';
import {
  CANCELLATION_WINDOW_HOURS,
  CLASH_BOOKING_STATUSES,
  LATE_CANCELLATION_PENALTY_PCT,
  LEGAL_CONTACT,
  NATIONAL_EMERGENCY,
  PAYMENTS_ENABLED,
  formatZAR,
  rangesOverlap,
  categoryHasWearLife,
  type BookingDoc,
  type ReportReason,
  type ReviewDoc,
  type PublicProfileDoc,
} from '@makhe/shared';

export default function BookingDetailPage() {
  const { bookingId } = useParams();
  const { firebaseUser } = useAuth();
  const ref = bookingId ? doc(db, 'bookings', bookingId) : null;
  const { data: booking, loading, error } = useDocument<BookingDoc>(ref);
  const sellerRef = booking ? doc(db, 'publicProfiles', booking.sellerId) : null;
  const buyerRef = booking ? doc(db, 'publicProfiles', booking.buyerId) : null;
  const { data: sellerUser } = useDocument<PublicProfileDoc>(sellerRef);
  const { data: buyerUser } = useDocument<PublicProfileDoc>(buyerRef);
  const reviewDocRef = booking?.reviewId ? doc(db, 'reviews', booking.reviewId) : null;
  const { data: review } = useDocument<ReviewDoc>(reviewDocRef);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [longevityOpen, setLongevityOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportDefaultReason, setReportDefaultReason] = useState<ReportReason | undefined>();
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeMs, setProposeMs] = useState<number | null>(null);
  const [proposeNote, setProposeNote] = useState('');
  const [acceptClashConfirm, setAcceptClashConfirm] = useState(false);
  const [safetyCancelConfirm, setSafetyCancelConfirm] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const verifyingRef = useRef(false);

  // Opened from the stage-2 "how's it holding up?" notification deep-link.
  useEffect(() => {
    if (searchParams.get('longevity') !== '1' || !review) return;
    if (!review.longevityAnsweredAt && !review.longevityDismissed) setLongevityOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('longevity');
    setSearchParams(next, { replace: true });
  }, [review, searchParams, setSearchParams]);

  const clashQ = useMemo(() => {
    if (!booking || !firebaseUser || booking.sellerId !== firebaseUser.uid) return null;
    if (booking.status !== 'requested') return null;
    return query(
      collection(db, 'bookings'),
      where('sellerId', '==', booking.sellerId),
      where('status', 'in', [...CLASH_BOOKING_STATUSES]),
      limit(40),
    );
  }, [booking, firebaseUser]);
  const { data: sellerBookings } = useCollection<BookingDoc>(clashQ);

  const clashes = useMemo(() => {
    if (!booking) return [];
    const start = booking.scheduledStart.toMillis();
    const end = booking.scheduledEnd.toMillis();
    return sellerBookings.filter((b) => {
      if (b.id === booking.id) return false;
      return rangesOverlap(start, end, b.scheduledStart.toMillis(), b.scheduledEnd.toMillis());
    });
  }, [booking, sellerBookings]);

  const returnReference = searchParams.get('reference') ?? searchParams.get('trxref');
  useEffect(() => {
    if (!bookingId || !returnReference || !firebaseUser || verifyingRef.current) return;
    verifyingRef.current = true;
    callVerifyPayment({ bookingId, reference: returnReference })
      .then((res) => {
        if (res.data.ok) toast.success('Payment received — you’re all set!');
        else if (res.data.status !== 'abandoned') toast.error('Payment not confirmed yet.');
      })
      .catch(() => toast.error('Could not confirm payment. Use the Pay button to retry.'))
      .finally(() => {
        setSearchParams({}, { replace: true });
        verifyingRef.current = false;
      });
  }, [bookingId, returnReference, firebaseUser, setSearchParams]);

  if (loading) return <LoadingSkeleton className="m-6 h-40" />;
  if (error)
    return (
      <div className="p-6">
        <EmptyState
          title="Couldn’t load this booking"
          description="Something went wrong. Check your connection and try again."
        />
      </div>
    );
  if (!booking || !firebaseUser)
    return (
      <div className="p-6">
        <EmptyState title="Booking not found" description="This booking may no longer exist." />
      </div>
    );

  const isBuyer = booking.buyerId === firebaseUser.uid;
  const isSeller = booking.sellerId === firebaseUser.uid;

  const wrap = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Cancel goes through an explicit confirm, and we surface the actual refund /
  // penalty the server returns instead of a bare "Cancelled".
  const submitCancel = async () => {
    setBusy(true);
    try {
      const res = await callCancelBooking({ bookingId: booking.id });
      const refundZARcents = res.data.refundZARcents ?? 0;
      const penaltyZARcents = res.data.penaltyZARcents ?? 0;
      toast.success(
        refundZARcents > 0
          ? `Cancelled. Refund of ${formatZAR(refundZARcents)} is being processed.`
          : penaltyZARcents > 0
            ? `Cancelled. A ${formatZAR(penaltyZARcents)} late-cancellation fee applied.`
            : 'Cancelled.',
      );
      setCancelConfirm(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const hoursToStart = (booking.scheduledStart.toMillis() - Date.now()) / 36e5;
  const cancelPenaltyZAR =
    isBuyer && booking.status === 'accepted' && hoursToStart < CANCELLATION_WINDOW_HOURS
      ? Math.round((booking.pricing.totalZARcents * LATE_CANCELLATION_PENALTY_PCT) / 100)
      : 0;

  const tryAccept = () => {
    if (clashes.length > 0 && !acceptClashConfirm) {
      setAcceptClashConfirm(true);
      return;
    }
    void wrap(async () => {
      await callAcceptBooking({ bookingId: booking.id });
      setAcceptClashConfirm(false);
    }, 'Accepted');
  };

  const submitPropose = () => {
    if (!proposeMs) return toast.error('Pick a new time.');
    void wrap(async () => {
      await callProposeBookingTime({
        bookingId: booking.id,
        scheduledStart: proposeMs,
        note: proposeNote.trim() || undefined,
      });
      setProposeOpen(false);
      setProposeMs(null);
      setProposeNote('');
    }, 'New time sent to the buyer');
  };

  const openReport = (defaultReason?: ReportReason) => {
    setReportDefaultReason(defaultReason);
    setReportOpen(true);
  };

  const submitSafetyCancel = () => {
    void wrap(async () => {
      await callCancelBooking({
        bookingId: booking.id,
        cancellationReason: 'safety',
        reason: 'Safety concern',
      });
      setSafetyCancelConfirm(false);
    }, 'Cancelled — stay safe');
  };

  const counterpartyName = isBuyer
    ? booking.serviceSnapshot.sellerDisplayName
    : buyerUser?.displayName ?? 'Buyer';
  // In-app chat replaces phone-number sharing: available to both participants once
  // the booking is accepted.
  const showChat = ['accepted', 'in_progress', 'awaiting_confirmation', 'completed'].includes(
    booking.status,
  );

  const checkIn = booking.safetyCheckIn;
  const userCheckedIn = isBuyer ? !!checkIn?.buyerAt : !!checkIn?.sellerAt;
  const otherCheckedIn = isBuyer ? !!checkIn?.sellerAt : !!checkIn?.buyerAt;

  return (
    <section className="mx-auto max-w-2xl px-5 py-6 pb-10 md:px-6">
      <Link to="/bookings" className="inline-flex items-center gap-1 text-sm text-makhe-muted">
        ← Bookings
      </Link>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusChip
          label={BOOKING_STATUS_LABEL[booking.status]}
          tone={BOOKING_STATUS_TONE[booking.status]}
        />
        {booking.urgent && <StatusChip label="Urgent" tone="accent" />}
      </div>

      <h1
        className="mt-3 font-display text-3xl leading-tight"
        style={{ fontVariationSettings: '"opsz" 72, "wght" 450' }}
      >
        {booking.serviceSnapshot.title}
      </h1>
      <p className="text-sm text-makhe-muted">
        with {booking.serviceSnapshot.sellerDisplayName}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {sellerUser && (
          <InstitutionBadge institution={sellerUser.studentVerification?.institution} />
        )}
        {buyerUser && isSeller && (
          <InstitutionBadge institution={buyerUser.studentVerification?.institution} />
        )}
      </div>

      {['accepted', 'in_progress', 'awaiting_confirmation'].includes(booking.status) && (
        <div className="mt-6">
          <BookingSafetyPanel
            buyerInstitution={buyerUser?.studentVerification?.institution}
            sellerInstitution={sellerUser?.studentVerification?.institution}
            onReport={() => openReport('safety')}
            bookingId={booking.id}
          />
        </div>
      )}

      {booking.status === 'in_progress' && (
        <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
          <p className="text-sm font-medium text-makhe-ink">Feel unsafe right now?</p>
          <p className="mt-1 text-xs text-makhe-muted">
            Leave the situation. Call emergency services if you need immediate help.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={`tel:${NATIONAL_EMERGENCY.saps}`}>
              <Button type="button" variant="secondary" size="sm">
                Call SAPS {NATIONAL_EMERGENCY.saps}
              </Button>
            </a>
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => setSafetyCancelConfirm(true)}
            >
              Cancel for safety
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => openReport('safety')}>
              Report
            </Button>
          </div>
        </div>
      )}

      {checkIn && ['accepted', 'in_progress'].includes(booking.status) && (
        <div className="mt-4 rounded-lg border border-makhe-line p-4">
          <p className="text-sm font-medium">Late-night safety check-in</p>
          <ul className="mt-2 space-y-1 text-xs text-makhe-muted">
            <li className={checkIn.buyerAt ? 'text-makhe-forest' : ''}>
              {checkIn.buyerAt ? '✓' : '○'} Buyer {checkIn.buyerAt ? 'checked in' : 'not yet'}
            </li>
            <li className={checkIn.sellerAt ? 'text-makhe-forest' : ''}>
              {checkIn.sellerAt ? '✓' : '○'} Seller {checkIn.sellerAt ? 'checked in' : 'not yet'}
            </li>
          </ul>
          {checkIn.status === 'pending' && (
            <>
              {!userCheckedIn ? (
                <Button
                  className="mt-3"
                  size="sm"
                  loading={busy}
                  onClick={() =>
                    wrap(
                      () => callConfirmSafetyCheckIn({ bookingId: booking.id }),
                      'Check-in recorded',
                    )
                  }
                >
                  I&apos;m safe / on my way
                </Button>
              ) : (
                <p className="mt-3 text-xs text-makhe-muted">
                  {otherCheckedIn
                    ? 'Both parties checked in — you can start when ready.'
                    : 'Waiting for the other person to check in.'}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="mt-6">
        <BookingStatusStepper status={booking.status} />
      </div>

      {(() => {
        const next = bookingNextAction(booking, isBuyer ? 'buyer' : 'seller');
        if (!next) return null;
        const pay = paymentStatusCopy(booking);
        const showPayBody =
          isBuyer &&
          PAYMENTS_ENABLED &&
          (next === 'Pay now' ||
            next === 'Pay to settle' ||
            next === 'Pay again' ||
            next === 'Checking payment' ||
            pay.title === 'Held' ||
            pay.title === 'Pay again');
        return (
          <div className="mt-4 rounded-xl border border-makhe-brand/25 bg-makhe-brand/5 px-4 py-3">
            <p className="text-[11px] uppercase tracking-widest text-makhe-muted">Next</p>
            <p className="mt-1 font-display text-lg text-makhe-ink">{next}</p>
            {showPayBody && (
              <p className="mt-1 text-sm text-makhe-muted">{pay.body}</p>
            )}
          </div>
        );
      })()}

      {booking.status === 'counter_proposed' && booking.counterProposal && (
        <div className="mt-6 rounded-lg border border-makhe-ochre/40 bg-makhe-ochre/10 p-4">
          <p className="text-[11px] uppercase tracking-widest text-makhe-ochre">
            Creator suggested a new time
          </p>
          <p className="mt-2 font-display text-xl">
            {formatBookingWhen(booking.counterProposal.scheduledStart.toDate?.())}
          </p>
          <p className="mt-1 text-sm text-makhe-muted line-through">
            Original · {formatBookingWhen(booking.scheduledStart.toDate?.())}
          </p>
          {booking.counterProposal.note && (
            <p className="mt-2 text-sm text-makhe-ink/80">“{booking.counterProposal.note}”</p>
          )}
          {isBuyer && (
            <div className="mt-4 space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <Button
                  loading={busy}
                  onClick={() =>
                    wrap(
                      () => callAcceptCounterProposal({ bookingId: booking.id }),
                      'New time accepted',
                    )
                  }
                >
                  Accept new time
                </Button>
                <Button
                  variant="ghost"
                  loading={busy}
                  onClick={() =>
                    wrap(
                      () =>
                        callCancelBooking({
                          bookingId: booking.id,
                          reason: 'Declined counter-proposal',
                        }),
                      'Booking cancelled',
                    )
                  }
                >
                  Cancel booking
                </Button>
              </div>
              <Button
                variant="danger"
                className="w-full"
                loading={busy}
                onClick={() => setSafetyCancelConfirm(true)}
              >
                Cancel for safety (no penalty)
              </Button>
            </div>
          )}
          {isSeller && (
            <p className="mt-3 text-xs text-makhe-muted">Waiting for the buyer to accept this time.</p>
          )}
        </div>
      )}

      <Card className="mt-6 space-y-2 text-sm">
        <p>
          <strong>When:</strong> {formatBookingWhen(booking.scheduledStart.toDate?.())} (
          {booking.serviceSnapshot.pricingTier.durationMinutes} min)
        </p>
        <p>
          <strong>Where:</strong> {booking.location.type.replace('_', ' ')}
          {booking.location.residence && ` · ${booking.location.residence}`}
          {booking.location.address && ` · ${booking.location.address}`}
        </p>
        {booking.location.notes && (
          <p>
            <strong>Notes:</strong> {booking.location.notes}
          </p>
        )}
        {booking.customisationNotes && (
          <p>
            <strong>Customisation:</strong> {booking.customisationNotes}
          </p>
        )}
      </Card>

      {showChat && (
        <BookingMessages
          bookingId={booking.id}
          currentUid={firebaseUser.uid}
          counterpartyName={counterpartyName}
        />
      )}

      {isSeller && booking.status === 'requested' && clashes.length > 0 && (
        <div className="mt-4 rounded-lg border border-makhe-primary/30 bg-makhe-primary/10 p-4">
          <p className="text-[11px] uppercase tracking-widest text-makhe-primary">
            Diary clash
          </p>
          <p className="mt-2 text-sm text-makhe-ink/90">
            You already have {clashes.length === 1 ? 'a booking' : `${clashes.length} bookings`}{' '}
            overlapping this time. You can still accept, or propose another slot.
          </p>
          <ul className="mt-3 space-y-1.5 text-xs text-makhe-muted">
            {clashes.slice(0, 3).map((c) => (
              <li key={c.id}>
                · {formatBookingWhen(c.scheduledStart.toDate?.())} — {c.serviceSnapshot.title} (
                {BOOKING_STATUS_LABEL[c.status]})
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4">
        <PriceBreakdown pricing={booking.pricing} showPayout={isSeller} />
        <p className="mt-2 text-xs text-makhe-muted/80">
          {PAYMENTS_ENABLED ? (
            <>
              {paymentStatusCopy(booking).title}
              {booking.payment.timing === 'after' &&
                booking.payment.status !== 'paid' &&
                ' · after the service'}
              {booking.payout?.status === 'released' ? ' · creator paid' : ''}
            </>
          ) : (
            paymentStatusCopy(booking).body
          )}
        </p>
        {PAYMENTS_ENABLED &&
          isBuyer &&
          booking.payment.status !== 'paid' &&
          booking.status === 'requested' && (
            <p className="mt-3 text-xs text-makhe-muted">
              You’ll be able to pay as soon as the seller accepts — no money moves before that.
            </p>
          )}
        {PAYMENTS_ENABLED &&
          isBuyer &&
          booking.payment.status !== 'paid' &&
          booking.payment.status !== 'refunded' &&
          !['requested', 'counter_proposed', 'declined', 'cancelled_by_buyer', 'cancelled_by_seller'].includes(
            booking.status,
          ) &&
          // At end-of-job, Pay is the confirmation-block primary — don't duplicate.
          booking.status !== 'awaiting_confirmation' && (
            <Button
              className="mt-3 w-full"
              loading={busy}
              onClick={() =>
                wrap(async () => {
                  const res = await callPayBooking({ bookingId: booking.id, origin: window.location.origin });
                  window.location.assign(res.data.paymentUrl);
                }, 'Redirecting to payment…')
              }
            >
              {booking.payment.paystackReference
                ? `Pay again · ${formatZAR(booking.pricing.totalZARcents)}`
                : `Pay ${formatZAR(booking.pricing.totalZARcents)}`}
            </Button>
          )}
      </div>

      {isSeller && booking.status === 'requested' && !proposeOpen && (
        <div className="mt-6 space-y-3">
          {acceptClashConfirm && (
            <div className="rounded-md border border-makhe-line bg-makhe-paper p-3 text-sm">
              <p className="font-medium">Accept anyway?</p>
              <p className="mt-1 text-xs text-makhe-muted">
                You’ll have overlapping bookings. Confirm only if you can cover both.
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" loading={busy} onClick={tryAccept}>
                  Yes, accept
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAcceptClashConfirm(false)}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
          {!acceptClashConfirm && (
            <div className="grid gap-2 sm:grid-cols-3">
              <Button loading={busy} onClick={tryAccept}>
                Accept
              </Button>
              <Button variant="secondary" onClick={() => setProposeOpen(true)}>
                Propose time
              </Button>
              <Button
                variant="danger"
                loading={busy}
                onClick={() =>
                  wrap(() => callDeclineBooking({ bookingId: booking.id }), 'Declined')
                }
              >
                Decline
              </Button>
            </div>
          )}
        </div>
      )}

      {isSeller && booking.status === 'requested' && proposeOpen && (
        <div className="mt-6 space-y-4 rounded-lg border border-makhe-line bg-makhe-paper p-4">
          <div>
            <p className="eyebrow">Counter-propose</p>
            <h3 className="mt-1 font-display text-xl">Suggest another slot</h3>
            <p className="mt-1 text-sm text-makhe-muted">
              Within your hours. The buyer can accept this time or cancel.
            </p>
          </div>
          <BookingSlotPicker
            sellerId={booking.sellerId}
            durationMinutes={booking.serviceSnapshot.pricingTier.durationMinutes}
            valueMs={proposeMs}
            onChange={setProposeMs}
          />
          <Textarea
            label="Note to buyer (optional)"
            rows={2}
            value={proposeNote}
            onChange={(e) => setProposeNote(e.target.value)}
            placeholder="I have a booking until 15:00 — does 15:30 work?"
          />
          <div className="flex gap-2">
            <Button loading={busy} onClick={submitPropose} disabled={!proposeMs}>
              Send new time
            </Button>
            <Button variant="ghost" onClick={() => setProposeOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {isSeller && booking.status === 'accepted' && (
        <div className="mt-6 grid gap-2">
          <Button
            loading={busy}
            onClick={() => wrap(() => callMarkInProgress({ bookingId: booking.id }), 'Started')}
          >
            Start service
          </Button>
        </div>
      )}

      {isSeller && booking.status === 'in_progress' && (
        <div className="mt-6">
          <Button
            loading={busy}
            onClick={() => wrap(() => callMarkServiceDone({ bookingId: booking.id }), 'Marked done')}
          >
            I&apos;m done. Ask the buyer to confirm.
          </Button>
        </div>
      )}

      {isBuyer && booking.status === 'awaiting_confirmation' && (
        <div className="mt-6 grid gap-2">
          {PAYMENTS_ENABLED && booking.payment.status !== 'paid' ? (
            // Unpaid: Pay is the only primary. For pay-after, payment auto-completes
            // the booking (nothing was escrowed). For pay-now, pay then confirm.
            <Button
              loading={busy}
              onClick={() =>
                wrap(async () => {
                  const res = await callPayBooking({ bookingId: booking.id, origin: window.location.origin });
                  window.location.assign(res.data.paymentUrl);
                }, 'Redirecting to payment…')
              }
            >
              {booking.payment.paystackReference
                ? `Pay again · ${formatZAR(booking.pricing.totalZARcents)}`
                : `Pay ${formatZAR(booking.pricing.totalZARcents)}`}
            </Button>
          ) : (
            <Button
              loading={busy}
              onClick={() =>
                wrap(() => callConfirmCompleted({ bookingId: booking.id }), 'Confirmed')
              }
            >
              {PAYMENTS_ENABLED
                ? "Yes, it's done. Release payment."
                : 'Confirm service completed'}
            </Button>
          )}
          <Button variant="danger" onClick={() => setDisputeOpen(true)}>
            Something went wrong
          </Button>
        </div>
      )}

      {(isBuyer || isSeller) &&
        ['requested', 'counter_proposed', 'accepted'].includes(booking.status) &&
        !(isBuyer && booking.status === 'counter_proposed') && (
          <div className="mt-4">
            {cancelPenaltyZAR > 0 && (
              <p className="mb-2 text-xs text-makhe-ochre">
                Cancelling within {CANCELLATION_WINDOW_HOURS}h costs you{' '}
                {formatZAR(cancelPenaltyZAR)} ({LATE_CANCELLATION_PENALTY_PCT}%).
              </p>
            )}
            <Button
              variant="ghost"
              loading={busy}
              onClick={() => setCancelConfirm(true)}
            >
              Cancel booking
            </Button>
            <Button
              variant="danger"
              className="mt-2"
              loading={busy}
              onClick={() => setSafetyCancelConfirm(true)}
            >
              Cancel for safety (no penalty)
            </Button>
          </div>
        )}

      {cancelConfirm && (
        <div className="mt-4 rounded-lg border border-makhe-line bg-makhe-surface p-4">
          <p className="text-sm font-medium">Cancel this booking?</p>
          <p className="mt-2 text-xs text-makhe-muted">
            {cancelPenaltyZAR > 0
              ? `Cancelling within ${CANCELLATION_WINDOW_HOURS}h of the start incurs a ${formatZAR(
                  cancelPenaltyZAR,
                )} (${LATE_CANCELLATION_PENALTY_PCT}%) late-cancellation fee; the rest is refunded.`
              : booking.payment.status === 'paid'
                ? 'Your payment will be refunded.'
                : 'This ends the booking. No payment has been taken.'}
          </p>
          <div className="mt-3 flex gap-2">
            <Button variant="danger" size="sm" loading={busy} onClick={submitCancel}>
              Yes, cancel
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCancelConfirm(false)}>
              Keep booking
            </Button>
          </div>
        </div>
      )}

      {safetyCancelConfirm && (
        <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
          <p className="text-sm font-medium">Cancel for safety?</p>
          <p className="mt-2 text-xs text-makhe-muted">
            This ends the booking with no late-cancel penalty. Makhe will open a safety report for
            our team to review. If you are in immediate danger, call SAPS{' '}
            <a href={`tel:${NATIONAL_EMERGENCY.saps}`} className="underline">
              {NATIONAL_EMERGENCY.saps}
            </a>{' '}
            first.
          </p>
          <div className="mt-3 flex gap-2">
            <Button variant="danger" size="sm" loading={busy} onClick={submitSafetyCancel}>
              Yes, cancel for safety
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSafetyCancelConfirm(false)}>
              Back
            </Button>
          </div>
        </div>
      )}

      {isBuyer && booking.status === 'completed' && !booking.reviewId && (
        <Button className="mt-6 w-full" onClick={() => setReviewOpen(true)}>
          Leave a review
        </Button>
      )}

      {isBuyer &&
        booking.reviewId &&
        review &&
        categoryHasWearLife(booking.serviceSnapshot.category) &&
        !review.longevityAnsweredAt &&
        !review.longevityDismissed && (
          <Button variant="secondary" className="mt-6 w-full" onClick={() => setLongevityOpen(true)}>
            Add how long it lasted
          </Button>
        )}

      <button
        onClick={() => openReport()}
        className="mt-6 text-xs text-makhe-muted/60 hover:text-makhe-primary"
      >
        Report a problem
      </button>

      <div className="mt-8 rounded-lg border border-makhe-line p-4 text-xs text-makhe-muted">
        Need help? <a href={`mailto:${LEGAL_CONTACT.email}`}>{LEGAL_CONTACT.email}</a>
      </div>

      <ReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        bookingId={booking.id}
        category={booking.serviceSnapshot.category}
      />
      {booking.reviewId && (
        <ReviewLongevitySheet
          open={longevityOpen}
          onClose={() => setLongevityOpen(false)}
          reviewId={booking.reviewId}
          category={booking.serviceSnapshot.category}
        />
      )}
      <ReportModal
        open={reportOpen}
        onClose={() => {
          setReportOpen(false);
          setReportDefaultReason(undefined);
        }}
        defaultReason={reportDefaultReason}
        target={{
          targetBookingId: booking.id,
          targetUserId: isBuyer ? booking.sellerId : booking.buyerId,
        }}
      />
      <DisputeModal
        open={disputeOpen}
        onClose={() => setDisputeOpen(false)}
        bookingId={booking.id}
      />
    </section>
  );
}
